import { Box, Typography, Paper, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ArrowForwardIos, EvStation, Search } from '@mui/icons-material';

import { ColumnTitle, dateRender, Pagination, ScrollbarCustom, WrapTable } from '../../../../components';
import { useGetList } from '../../../../services/charge-sessions/useChargeSession';
import { IUserDetail } from '../../../../types/User';
import { useState } from 'react';
import { IChargingSession } from '../../../../types/ChargingSession';
import { formatPrice } from '../../../../utils/format';
import ChargeSessionStatus from '../../../../components/ChargerComponents/ChargeSessionStatus';
import Copyable from '../../../../components/Copyable';

const ChargeSessionStatusMapping = {
    completed: "Completed",
    charging: "Charging",
    holding: "Holding",
    fee_calculating: "Fee Calculating",
    noshow: "No Show",
    canceled: "Canceled"
}

export const ChargingSessions = ({ userDetail }: { userDetail: IUserDetail }) => {
    const [searchParams, setSearchParams] = useState<{ [key: string]: any }>({});
    const [page, setPage] = useState(1);
    const { data: chargingSessionsData, isLoading, isFetching } = useGetList(page, { per_page: 10, user_id: userDetail._id, by_type: 'exclude_upcoming_or_canceled_reservation', ...searchParams });
    const { data: tableData, total, per_page } = chargingSessionsData || { data: [], total: 0, per_page: 10 };

    const onSearch = (params: { [key: string]: any }) => {
        setSearchParams(params);
        setPage(1);
    }
    return (
        <Box sx={{ color: 'white', p: 4 }}>
            <Stack direction="row" alignItems="center" mb={4} spacing={2}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 32, mr: 1 }}>
                        <EvStation style={{ fill: '#FFD600', width: 32, height: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>Charging Session</Typography>
                        <Typography variant="body2" color="gray">Get a detailed breakdown of user charging sessions.</Typography>
                    </Box>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<Search />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Search
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Download CSV
                    </Button>
                    <IconButton sx={{ borderRadius: 3, color: 'white', border: '1px solid #444', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        <FilterListIcon />
                    </IconButton>
                </Stack>
            </Stack>
            <Paper elevation={0} sx={{ bgcolor: '#191919', borderRadius: 4, p: 4 }}>
                <Box mb={2}>
                    <WrapTable p={"5px 21px"} height="100%" marginBottom={3} isLoading={isLoading || isFetching}>
                        <TableContainer>
                            <ScrollbarCustom autoHide>
                                <Table sx={{ minWidth: 800, marginBottom: "10px" }}>
                                    <TableHead>
                                        <TableRow>
                                            <ColumnTitle sx={{ pl: 0 }}>SN</ColumnTitle>
                                            <ColumnTitle
                                                filter
                                                onChangeText={value => onSearch({ ...searchParams, ampeco_session_id: value })}
                                            >Session ID</ColumnTitle>
                                            <ColumnTitle
                                                filter
                                                onChangeText={value => onSearch({ ...searchParams, charge_point_name: value })}
                                            >Charger name</ColumnTitle>
                                            <ColumnTitle sx={{ minWidth: '250px' }}>Date & time unplug</ColumnTitle>
                                            <ColumnTitle >Charger type</ColumnTitle>
                                            <ColumnTitle >Charging type</ColumnTitle>
                                            <ColumnTitle align='center'
                                                filterOption={Object.entries(ChargeSessionStatusMapping).map(([value, label]) => ({ value, label }))}
                                                onChangeText={value => onSearch({ ...searchParams, status: value })}
                                            >Status</ColumnTitle>
                                            <ColumnTitle >Energy</ColumnTitle>
                                            <ColumnTitle >Points</ColumnTitle>
                                            <ColumnTitle >Charging rate</ColumnTitle>
                                            <ColumnTitle sx={{ minWidth: '200px' }} >Transaction</ColumnTitle>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableData.map((row: IChargingSession, index: number) => (
                                            <TableRow key={row._id}>
                                                <TableCell sx={{ pl: 0 }}>{(page - 1) * per_page + index + 1}</TableCell>
                                                <TableCell><Copyable text={row.ampeco_session_id} /></TableCell>
                                                <TableCell>{row.charge_point_name}</TableCell>
                                                <TableCell>
                                                    {row.session_started_at && (
                                                        <>
                                                            {`Started: ${dateRender(row.session_started_at, true)}`}
                                                            {(row.session_stopped_at || row.idling_started_at || row.unplugged_at) && <br />}
                                                        </>
                                                    )}
                                                    {row.session_stopped_at && (
                                                        <>
                                                            {`Stopped: ${dateRender(row.session_stopped_at, true)}`}
                                                            {(row.idling_started_at || row.unplugged_at) && <br />}
                                                        </>
                                                    )}
                                                    {row.idling_started_at && (
                                                        <>
                                                            {`Start idling: ${dateRender(row.idling_started_at, true)}`}
                                                            {row.unplugged_at && <br />}
                                                        </>
                                                    )}
                                                    {row.unplugged_at && (
                                                        <>
                                                            {row.unplugged_at && `Unplugged: ${dateRender(row.unplugged_at, true)}`}
                                                            {Boolean(row.charging_duration) && <br />}
                                                        </>
                                                    )}
                                                    {Boolean(row.charging_duration) && `Duration: ${row.charging_duration} mins`}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography alignItems={'center'} display={'flex'} fontWeight={600}>
                                                        <Box width={'24px'} height={'24px'} marginRight={'4px'}>
                                                            <img src="/images/ac-charger.png" alt="AC Charger" />
                                                        </Box>
                                                        {row.evse.current_type.toUpperCase()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{row.type?.toLocaleUpperCase()}</TableCell>
                                                <TableCell align='center'>
                                                    <ChargeSessionStatus chargeSession={row as unknown as ChargeSession} />
                                                </TableCell>
                                                <TableCell>{row.energy || 0} kwh</TableCell>
                                                <TableCell>{row.rewards?.reduce((acc: number, reward: any) => acc + reward.value, 0)}</TableCell>
                                                <TableCell>{formatPrice(row.price_kwh || 0)} /kWh <br /> (Type: {row.price_type})</TableCell>
                                                <TableCell>
                                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                                                        <Typography sx={{ textDecoration: 'underline' }}>
                                                            Charging: {formatPrice(row.charging_fee_with_gst || 0)}
                                                            <br />
                                                            Idling penalty: {formatPrice(row.idling_fee_with_gst || 0)}
                                                        </Typography>
                                                        <ArrowForwardIos />
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ScrollbarCustom>
                        </TableContainer>
                    </WrapTable>
                    <Pagination
                        page={page}
                        setPage={setPage}
                        totalItems={total || 0}
                        totalPage={Math.ceil((total || 0) / per_page)}
                        currentPage={page}
                    />
                </Box>
            </Paper >
        </Box >
    );
}
